// src/pages/sales/purchase-order-components/po-delivery-confirmation-components/DeliveryDetailsSection.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Grid,
  Paper,
  Chip,
  alpha,
  useTheme,
  Button,
  Snackbar,
  Alert,
  Container
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../utils/permissions';

const DeliveryDetailsSection = ({ deliveryDetails, setDeliveryDetails, onSave }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { companyId, id: poId } = useParams();
  const { canViewDeliveryDetails, canEditDeliveryDetails } = usePermissions();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);

  // Permission checks
  const canView = canViewDeliveryDetails();
  const canEdit = canEditDeliveryDetails();

  useEffect(() => {
    if (poId && poId !== 'new') {
      fetchDeliveryDetails();
    }
  }, [poId]);

  const fetchDeliveryDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('po_delivery_confirmations')
        .select('actual_delivery_date, delivery_complete, shortage_details, quantity_short, condition_when_delivered, condition_notes, issue_severity, resolution_required, resolution_notes')
        .eq('purchase_order_id', poId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // Ignore "not found" error
      
      if (data) {
        setDeliveryDetails(prev => ({
          ...prev,
          actualDeliveryDate: data.actual_delivery_date || '',
          deliveryComplete: data.delivery_complete || 'yes',
          shortageDetails: data.shortage_details || '',
          quantityShort: data.quantity_short ? String(data.quantity_short) : '',
          conditionWhenDelivered: data.condition_when_delivered || 'good',
          conditionNotes: data.condition_notes || '',
          issueSeverity: data.issue_severity || 'none',
          resolutionRequired: data.resolution_required || 'no',
          resolutionNotes: data.resolution_notes || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching delivery details:', err);
    }
  };

  const saveDeliveryDetails = async () => {
    setSaving(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Check if record exists
      const { data: existing } = await supabase
        .from('po_delivery_confirmations')
        .select('id')
        .eq('purchase_order_id', poId)
        .single();

      const payload = {
        actual_delivery_date: deliveryDetails.actualDeliveryDate || null,
        delivery_complete: deliveryDetails.deliveryComplete || 'yes',
        shortage_details: deliveryDetails.shortageDetails || null,
        quantity_short: deliveryDetails.quantityShort ? parseInt(deliveryDetails.quantityShort) : null,
        condition_when_delivered: deliveryDetails.conditionWhenDelivered || null,
        condition_notes: deliveryDetails.conditionNotes || null,
        issue_severity: deliveryDetails.issueSeverity || 'none',
        resolution_required: deliveryDetails.resolutionRequired || 'no',
        resolution_notes: deliveryDetails.resolutionNotes || null,
        delivery_details_completed: true,
        updated_at: new Date().toISOString()
      };

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .update(payload)
          .eq('purchase_order_id', poId);

        if (error) throw error;
      } else {
        // Create new record
        const { error } = await supabase
          .from('po_delivery_confirmations')
          .insert({
            company_id: companyId,
            purchase_order_id: poId,
            created_by: user.id,
            ...payload
          });

        if (error) throw error;
      }

      setSnackbar({ open: true, message: 'Delivery details saved successfully', severity: 'success' });
      
      // Reload completion flags
      if (onSave) onSave();
    } catch (err) {
      console.error('Error saving delivery details:', err);
      setSnackbar({ open: true, message: 'Failed to save delivery details', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setDeliveryDetails({ ...deliveryDetails, [field]: value });
  };

  const issueSeverityOptions = [
    { value: 'none', label: 'None', color: 'success', icon: <CheckIcon /> },
    { value: 'low', label: 'Low', color: 'info', icon: <WarningIcon /> },
    { value: 'medium', label: 'Medium', color: 'warning', icon: <WarningIcon /> },
    { value: 'high', label: 'High', color: 'error', icon: <ErrorIcon /> },
    { value: 'critical', label: 'Critical', color: 'error', icon: <ErrorIcon /> }
  ];

  const conditionOptions = [
    { value: 'excellent', label: 'Excellent', color: 'success' },
    { value: 'good', label: 'Good', color: 'success' },
    { value: 'fair', label: 'Fair', color: 'warning' },
    { value: 'poor', label: 'Poor', color: 'error' },
    { value: 'damaged', label: 'Damaged', color: 'error' }
  ];

  // Access Denied Card
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Delivery Details Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Delivery Details. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Card sx={{
      mb: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 20px ${alpha(theme.palette.grey[500], 0.15)}`
      }
    }}>
      <CardContent sx={{ p: 4 }}>
        {/* Section Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
            }}>
              <ShippingIcon sx={{ fontSize: 28, color: 'info.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'info.main', mb: 0.5 }}>
                Delivery Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Information captured when customer receives goods
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => canEdit && saveDeliveryDetails()}
            disabled={saving || !canEdit}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              opacity: canEdit ? 1 : 0.3,
              pointerEvents: canEdit ? 'auto' : 'none',
              cursor: canEdit ? 'pointer' : 'not-allowed'
            }}
          >
            {saving ? 'Saving...' : 'Save Details'}
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Actual Delivery Date */}
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Actual Delivery Date"
                value={deliveryDetails.actualDeliveryDate ? dayjs(deliveryDetails.actualDeliveryDate) : null}
                onChange={(newValue) => canEdit && handleChange('actualDeliveryDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
                disabled={!canEdit}
                sx={{ 
                  width: '100%',
                  opacity: canEdit ? 1 : 0.6,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Was Delivery Complete */}
          <Grid item xs={12}>
            <Paper sx={{
              p: 2.5,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.grey[50], 0.5),
              opacity: canEdit ? 1 : 0.6
            }}>
              <FormControl component="fieldset" fullWidth disabled={!canEdit}>
                <FormLabel sx={{ fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                  Was delivery complete?
                </FormLabel>
                <RadioGroup
                  value={deliveryDetails.deliveryComplete || 'yes'}
                  onChange={(e) => canEdit && handleChange('deliveryComplete', e.target.value)}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckIcon sx={{ fontSize: 20, color: 'success.main' }} />
                        <span>Yes, Complete</span>
                      </Box>
                    }
                    sx={{ mb: 1 }}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                        <span>No, Incomplete</span>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Paper>
          </Grid>

          {/* If Incomplete - Shortage Details */}
          {deliveryDetails.deliveryComplete === 'no' && (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Shortage Details"
                  multiline
                  rows={2}
                  value={deliveryDetails.shortageDetails || ''}
                  onChange={(e) => canEdit && handleChange('shortageDetails', e.target.value)}
                  disabled={!canEdit}
                  placeholder="Describe what items are missing or short..."
                  sx={{
                    opacity: canEdit ? 1 : 0.6,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quantity Short"
                  type="number"
                  value={deliveryDetails.quantityShort || ''}
                  onChange={(e) => canEdit && handleChange('quantityShort', e.target.value)}
                  disabled={!canEdit}
                  placeholder="Number of units/cases short"
                  sx={{
                    opacity: canEdit ? 1 : 0.6,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Condition of Goods When Delivered */}
          <Grid item xs={12}>
            <FormControl fullWidth disabled={!canEdit}>
              <FormLabel sx={{ fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                Condition of Goods When Delivered
              </FormLabel>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', opacity: canEdit ? 1 : 0.6 }}>
                {conditionOptions.map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    onClick={() => canEdit && handleChange('conditionWhenDelivered', option.value)}
                    color={deliveryDetails.conditionWhenDelivered === option.value ? option.color : 'default'}
                    variant={deliveryDetails.conditionWhenDelivered === option.value ? 'filled' : 'outlined'}
                    sx={{
                      px: 2,
                      py: 2.5,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: canEdit ? 'pointer' : 'not-allowed',
                      pointerEvents: canEdit ? 'auto' : 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette[option.color].main, 0.3)}`
                      }
                    }}
                  />
                ))}
              </Box>
            </FormControl>
          </Grid>

          {/* Condition Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Condition Notes"
              multiline
              rows={3}
              value={deliveryDetails.conditionNotes || ''}
              onChange={(e) => canEdit && handleChange('conditionNotes', e.target.value)}
              disabled={!canEdit}
              placeholder="Additional details about the condition of goods..."
              sx={{
                opacity: canEdit ? 1 : 0.6,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>

          {/* Issue Severity */}
          <Grid item xs={12}>
            <FormControl fullWidth disabled={!canEdit}>
              <FormLabel sx={{ fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                Issue Severity
              </FormLabel>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', opacity: canEdit ? 1 : 0.6 }}>
                {issueSeverityOptions.map((option) => (
                  <Chip
                    key={option.value}
                    icon={option.icon}
                    label={option.label}
                    onClick={() => canEdit && handleChange('issueSeverity', option.value)}
                    color={deliveryDetails.issueSeverity === option.value ? option.color : 'default'}
                    variant={deliveryDetails.issueSeverity === option.value ? 'filled' : 'outlined'}
                    sx={{
                      px: 2,
                      py: 2.5,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: canEdit ? 'pointer' : 'not-allowed',
                      pointerEvents: canEdit ? 'auto' : 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette[option.color].main, 0.3)}`
                      }
                    }}
                  />
                ))}
              </Box>
            </FormControl>
          </Grid>

          {/* Resolution Required */}
          {deliveryDetails.issueSeverity !== 'none' && (
            <>
              <Grid item xs={12}>
                <Paper sx={{
                  p: 2.5,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  opacity: canEdit ? 1 : 0.6
                }}>
                  <FormControl component="fieldset" disabled={!canEdit}>
                    <FormLabel sx={{ fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                      Resolution Required?
                    </FormLabel>
                    <RadioGroup
                      row
                      value={deliveryDetails.resolutionRequired || 'no'}
                      onChange={(e) => canEdit && handleChange('resolutionRequired', e.target.value)}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes, Action Needed" />
                      <FormControlLabel value="no" control={<Radio />} label="No Action Needed" />
                    </RadioGroup>
                  </FormControl>
                </Paper>
              </Grid>

              {deliveryDetails.resolutionRequired === 'yes' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Resolution Notes"
                    multiline
                    rows={3}
                    value={deliveryDetails.resolutionNotes || ''}
                    onChange={(e) => canEdit && handleChange('resolutionNotes', e.target.value)}
                    disabled={!canEdit}
                    placeholder="Describe what action is needed to resolve the issue..."
                    sx={{
                      opacity: canEdit ? 1 : 0.6,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </CardContent>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default DeliveryDetailsSection;